import { useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";

export default function About() {
  useEffect(() => {
    document.title = "关于我们 - 大师兄影视";
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <ScrollToTop />
      <Navbar />

      <main className="pt-20 pb-12">
        <div className="max-w-[1000px] mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: "首页", path: "/" },
                { label: "关于我们" },
              ]}
            />
          </div>

          <section className="mb-12">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
              关于大师兄影视
            </h1>
            <p className="text-sm text-foreground-600 leading-relaxed max-w-2xl">
              大师兄影视是一个专注于影视内容资讯的平台，致力于为用户提供最新、最快、最全的影视信息。从热门电影到精品电视剧，从综艺节目到动漫佳作，我们用心挑选每一部作品，让你不错过任何精彩。
            </p>
          </section>

          <section className="mb-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">品牌故事</h2>
                <div className="space-y-3 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    大师兄影视创立于2018年，名字取自"大师兄"这一亲切称呼——就像学生时代那个什么都知道、什么都愿意帮你的大师兄一样，我们希望成为用户在影视世界中最可靠的伙伴。
                  </p>
                  <p>
                    六年来，我们从一个小小的影视资讯站，成长为拥有百万级日活用户的综合性影视平台。我们的团队汇聚了来自影视行业、互联网技术和内容创作领域的优秀人才，共同为用户打造极致的观影信息体验。
                  </p>
                  <p>
                    我们始终坚信，好的内容值得被更多人看到。大师兄影视不仅提供影视信息，更是一个影视爱好者的社区——在这里，你可以发现好片、交流感受、分享热爱。
                  </p>
                </div>
              </div>

              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20movie%20entertainment%20company%20office%20interior%20with%20film%20posters%20on%20wall%2C%20creative%20workspace%2C%20warm%20lighting%2C%20cozy%20atmosphere%2C%20professional%20yet%20relaxed%20environment%2C%20cinematic%20decor&width=800&height=600&seq=about-story&orientation=landscape"
                  alt="大师兄影视品牌故事"
                  className="w-full h-56 md:h-72 object-cover rounded-xl"
                />
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">核心优势</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                {
                  icon: "ri-flashlight-line",
                  title: "更新快速",
                  desc: "第一手影视资讯，每日同步更新，绝不让你等待。院线新片、热播剧集上线即推。",
                },
                {
                  icon: "ri-database-2-line",
                  title: "资源丰富",
                  desc: "收录超过10万部影视作品，涵盖电影、电视剧、综艺、动漫、纪录片等全品类。",
                },
                {
                  icon: "ri-4k-line",
                  title: "品质保证",
                  desc: "精选高清片源，提供1080P至4K超清画质，流畅不卡顿的极致观影体验。",
                },
                {
                  icon: "ri-user-heart-line",
                  title: "用户至上",
                  desc: "始终以用户需求为核心，持续优化产品体验，千万用户的共同选择。",
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="flex flex-col items-center text-center p-4 md:p-5 rounded-xl bg-background-100"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 mb-3">
                    <i className={`${item.icon} text-xl text-primary-600`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-2">{item.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">用户口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                {
                  name: "追剧达人小王",
                  rating: 5,
                  comment: "大师兄影视真的太快了！每次新剧上线我都是第一个知道的，画质也超级清晰，强烈推荐！",
                  date: "2026-07-28",
                },
                {
                  name: "电影爱好者老张",
                  rating: 5,
                  comment: "用了三年了，资源全、更新快、不卡顿。从国产片到好莱坞大片，想看的基本都能找到。",
                  date: "2026-07-25",
                },
                {
                  name: "综艺迷小李",
                  rating: 5,
                  comment: "综艺节目更新特别及时，我最喜欢的几档综艺都能在这里第一时间看到。推荐给所有综艺爱好者！",
                  date: "2026-07-22",
                },
              ].map((review) => (
                <div key={review.name} className="p-4 md:p-5 rounded-xl bg-background-100">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold text-sm">
                      {review.name.charAt(0)}
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-950">{review.name}</h4>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        {Array.from({ length: review.rating }).map((_, i) => (
                          <i key={i} className="ri-star-fill text-xs text-accent-500"></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{review.comment}</p>
                  <p className="text-xs text-foreground-400 mt-3">{review.date}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">常见问题</h2>
            <div className="space-y-3">
              {[
                {
                  q: "大师兄影视需要付费吗？",
                  a: "不需要。大师兄影视是一个完全免费的影视资讯平台，所有内容均可免费浏览，无需会员，永久免费。",
                },
                {
                  q: "影片更新频率是怎样的？",
                  a: "我们每天都会更新最新上映的电影、热播剧集和综艺节目。一般在正片上线后2小时内完成同步，确保你第一时间获取最新影视资讯。",
                },
                {
                  q: "支持哪些设备观看？",
                  a: "大师兄影视支持电脑、平板和手机等多种设备浏览。只要你有浏览器，就能随时随地访问我们的平台。",
                },
                {
                  q: "影片画质如何？",
                  a: "我们精选高清片源，提供1080P超清画质，部分热门影片支持4K分辨率。配合CDN全球加速技术，确保流畅不卡顿的观影体验。",
                },
              ].map((faq, index) => (
                <details key={index} className="group rounded-xl bg-background-100 overflow-hidden cursor-pointer">
                  <summary className="flex items-center justify-between p-4 md:p-5 text-sm font-medium text-foreground-950 list-none">
                    {faq.q}
                    <span className="w-5 h-5 flex items-center justify-center transition-transform duration-300 group-open:rotate-180">
                      <i className="ri-arrow-down-s-line text-foreground-400"></i>
                    </span>
                  </summary>
                  <div className="px-4 md:px-5 pb-4 md:pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">联系我们</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-5 md:p-6 rounded-xl bg-background-100">
                <h3 className="text-sm font-semibold text-foreground-950 mb-3">联系方式</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100">
                      <i className="ri-mail-line text-primary-600 text-sm"></i>
                    </div>
                    <span className="text-sm text-foreground-700">contact@dashixiong.tv</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100">
                      <i className="ri-wechat-line text-primary-600 text-sm"></i>
                    </div>
                    <span className="text-sm text-foreground-700">微信公众号：大师兄影视</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100">
                      <i className="ri-weibo-line text-primary-600 text-sm"></i>
                    </div>
                    <span className="text-sm text-foreground-700">微博：@大师兄影视官方</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100">
                      <i className="ri-bilibili-line text-primary-600 text-sm"></i>
                    </div>
                    <span className="text-sm text-foreground-700">B站：大师兄影视</span>
                  </div>
                </div>
              </div>

              <div className="p-5 md:p-6 rounded-xl bg-background-100">
                <h3 className="text-sm font-semibold text-foreground-950 mb-3">商务合作</h3>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  如果你有影视内容合作、广告投放或其他商务需求，欢迎通过以下方式与我们联系。我们期待与各界伙伴携手合作，共同推动影视文化传播。
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100">
                    <i className="ri-briefcase-line text-accent-600 text-sm"></i>
                  </div>
                  <span className="text-sm text-foreground-700">biz@dashixiong.tv</span>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "关于大师兄影视",
            "url": "https://www.dashixiong.tv/about",
            "isPartOf": {
              "@type": "WebSite",
              "name": "大师兄影视",
              "url": "https://www.dashixiong.tv/",
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "首页",
                  "item": "https://www.dashixiong.tv/",
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "关于我们",
                },
              ],
            },
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "大师兄影视需要付费吗？",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "不需要。大师兄影视是一个完全免费的影视资讯平台，所有内容均可免费浏览，无需会员，永久免费。",
                },
              },
              {
                "@type": "Question",
                "name": "影片更新频率是怎样的？",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "我们每天都会更新最新上映的电影、热播剧集和综艺节目。一般在正片上线后2小时内完成同步。",
                },
              },
              {
                "@type": "Question",
                "name": "支持哪些设备观看？",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "大师兄影视支持电脑、平板和手机等多种设备浏览。",
                },
              },
              {
                "@type": "Question",
                "name": "影片画质如何？",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "我们精选高清片源，提供1080P超清画质，部分热门影片支持4K分辨率。",
                },
              },
            ],
          }),
        }}
      />
    </div>
  );
}